import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';

// Actions
import { gameActions } from '../../actions';
// import { setupSocket, enterBackend, socketOn } from '../../helpers/sockets';

import Header from '../../components/Header';
import { DebriefMenu } from './debrief_menu';
// import { player } from '../../reducers/player';

// List of potential Events
const PART_1_EVENTS = [
  'Speak to Mariana.',
  'Speak to Maria.',
  'Speak to Laura.',
  'Speak to Roberta.',
  'Take a stroll.',
  'Speak to Sara at the park.',
  'Get Sara to vouch for you.',
  'Call Douglas Fischer.',
  'Get a massage.',
  'Get a haircut.',
  'Check out a competing store.',
  'Take a nap.',
  'Grab a meal.',
  'Grab some coffee.',
  'Listen to a podcast.',
  'Take a public speaking course.',
  'Complete a public speaking course.',
  'Take an emotional intelligence course.',
  'Complete an emotional intelligence course.',
  'Do internet research into Vitoria fashion.',
  'Go to the salon.',
  'Go to the menswear store.',
  "Check out Dillon's.",
  'Beeline for the food court.',
  'Grab a snack.',
  'Eat a full meal.',
  'Walk into the store.',
  '(SecretShopper) Look at the seasonal selection.',
  '(SecretShopper) Browse the discount rack.',
  'Reveal as Secret Shopper.',
  'Complete as Secret Shopper.',
  'Obtain financial records.',
  'Obtain HR records.',
  'Take a city tour.',
  'Meet with hometown friends.',
  'Meet with a past colleague.',
  'Go to the beach.',
];

const PART_2_EVENTS_DAY_1 = [
  '(Day 1) Ask for input.',
  '(Day 1) Visit competing stores.',
  '(Day 1) Follow your plan.',
  '(Day 1) Create monetary incentives.',
  '(Day 1) Threaten store closure.',
];

const PART_2_EVENTS_DAY_45 = [
  '(Day 45) Stay the course.',
  '(Day 45) Get tough.',
  '(Day 45) Abandon this plan.',
];

const GameHistogram = (props) => {
  // States
  // const [selectedRound, setSelectedRound] = useState(null);

  // Hooks
  const dispatch = useDispatch();
  const game = useSelector((state) => state.game);
  // const game_id = game?.data?._id;
  // const game_rounds = game?.data?.rounds;
  const { id } = useParams();
  const { t } = useTranslation();

  // Effects
  useEffect(() => {
    if (id) {
      dispatch(gameActions.fetchGames(id));
    }
  }, [dispatch, id]);

  // Logic: Histogram data
  // Data for the histogram chart is the list of results per player
  const playerDecisions = game?.data?.players?.reduce((acc, player) => {
    const results =
      player?.results
        ?.filter((r) => r?.EventType === 'quest_state_change')
        .map((r) => r?.Data?.questName) || [];
    acc = acc.concat(results);
    return acc;
  }, []);

  // Unique decisions
  const uniqueDecisions = [...new Set(playerDecisions)];

  // PART 1: filter out those that start with "(Day X)"
  const stageOneDecisions = uniqueDecisions.filter(
    (decision) => !decision.startsWith('(Day '),
  );

  // PART 1: Count the frequency of each decision
  const stageOneDecisionFrequency = stageOneDecisions.reduce(
    (acc, decision, di) => {
      const count = playerDecisions.filter((d) => d === decision).length;
      acc[di] = count;
      return acc;
    },
    [],
  );

  // PART 1: Non-executed events
  const stageOneNonExecutedEvents = PART_1_EVENTS.filter(
    (et) => !stageOneDecisions.includes(et),
  );

  // PART 2: DAY 1 only decisions: start with "(Day 1)"
  const part2_Day1_Decisions = uniqueDecisions.filter((decision) =>
    decision.startsWith('(Day 1)'),
  );

  const part2_Day1_Decisions_Frequency = part2_Day1_Decisions.reduce(
    (acc, decision, di) => {
      const count = playerDecisions.filter((d) => d === decision).length;
      acc[di] = count;
      return acc;
    },
    [],
  );
  // PART 2: DAY 1 - Non-executed events
  const part2_Day1_NonExecutedEvents = PART_2_EVENTS_DAY_1.filter(
    (et) => !part2_Day1_Decisions.includes(et),
  );

  // PART 2: DAY 45 - only decisions: start with "(Day 45)"
  const part2_Day45_Decisions = uniqueDecisions.filter((decision) =>
    decision.startsWith('(Day 45)'),
  );
  const part2_Day45_Decisions_Frequency = part2_Day45_Decisions.reduce(
    (acc, decision, di) => {
      const count = playerDecisions.filter((d) => d === decision).length;
      acc[di] = count;
      return acc;
    },
    [],
  );
  // PART 2: DAY 45 - Non-executed events
  const part2_Day45_NonExecutedEvents = PART_2_EVENTS_DAY_45.filter(
    (et) => !part2_Day45_Decisions.includes(et),
  );

  return (
    <div>
      <Header />

      <h2 className='mt-3 sec-title fs-3'>
        {t('Histogram')}
        <small className='float-end'>
          <DebriefMenu game_id={game.data._id} location={props.location} />
        </small>
      </h2>

      <div className='mt-3 pb-2 row'>
        <div className='col-12'>
          <div
            className='row flex-column bg-white mb-3 pt-3 pb-5'
            style={{
              minHeight: '78vh',
            }}>
            {/* The histogram chart */}
            <div className='col-12'>
              <div className='card bg-light p-3'>
                <h3 className='fs-5 mb-3'>Part 1: Actions</h3>
                <HighchartsReact
                  // ref={this.chartRef}
                  highcharts={Highcharts}
                  options={{
                    title: { text: '' },
                    chart: { type: 'column' },
                    credits: { enabled: false },
                    legend: { enabled: true },
                    xAxis: {
                      // title: { text: 'Employee Effort' },
                      labels: { style: { fontSize: '14px' } },
                      categories: stageOneDecisions,
                    },
                    yAxis: {
                      labels: { style: { fontSize: '14px' } },
                      title: { text: 'Count' },
                      allowDecimals: false,
                    },
                    // tooltip: {
                    //   formatter: function () {
                    //     const xLabel = this?.x === 50 ? '≥50' : this?.x;
                    //     return (
                    //       `<b>Employee Effort</b>: ${xLabel}<br>` +
                    //       `<b>Cumulative Frequency</b>: ${this.y}`
                    //     );
                    //   },
                    // },
                    series: [
                      {
                        name: 'Events',
                        data: stageOneDecisionFrequency,
                      },
                    ],
                  }}
                />

                {/* Non-executed events */}
                <div className='card mt-3'>
                  <div className='card-body'>
                    <h5 className='fs-6 fw-bold'>Actions No One Pursued</h5>
                    <hr />
                    {stageOneNonExecutedEvents.length === 0 && (
                      <p>
                        All possible events were executed by at least one
                        player.
                      </p>
                    )}
                    <div
                      className='fst-italic'
                      style={{ fontFamily: 'monospace' }}>
                      {
                        stageOneNonExecutedEvents.length > 0 &&
                          stageOneNonExecutedEvents.map((ne, i) => (
                            <span
                              key={i}
                              className='badge text-bg-light border me-3 mb-2'>
                              {ne}
                            </span>
                          ))
                        // <ul>
                        //   {nonExecutedEvents.map((ne, i) => (
                        //     <li key={i}>{ne}</li>
                        //   ))}
                        // </ul>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Histogram: Part 2 - Day 1 */}
            <div className='col-12 mt-5'>
              <div className='card bg-light p-3'>
                <h3 className='fs-5 mb-3'>Part 2 - Day 1: Actions</h3>

                <HighchartsReact
                  // ref={this.chartRef}
                  highcharts={Highcharts}
                  options={{
                    title: { text: '' },
                    chart: { type: 'column' },
                    credits: { enabled: false },
                    legend: { enabled: true },
                    xAxis: {
                      // title: { text: 'Employee Effort' },
                      labels: { style: { fontSize: '14px' } },
                      categories: part2_Day1_Decisions,
                    },
                    yAxis: {
                      labels: { style: { fontSize: '14px' } },
                      title: { text: 'Count' },
                      allowDecimals: false,
                    },
                    // tooltip: {
                    //   formatter: function () {
                    //     const xLabel = this?.x === 50 ? '≥50' : this?.x;
                    //     return (
                    //       `<b>Employee Effort</b>: ${xLabel}<br>` +
                    //       `<b>Cumulative Frequency</b>: ${this.y}`
                    //     );
                    //   },
                    // },
                    series: [
                      {
                        name: 'Events',
                        data: part2_Day1_Decisions_Frequency,
                      },
                    ],
                  }}
                />

                {/* Non-executed events */}
                <div className='card mt-3'>
                  <div className='card-body'>
                    <h5 className='fs-6 fw-bold'>Actions No One Pursued</h5>
                    <hr />
                    {part2_Day1_NonExecutedEvents.length === 0 && (
                      <p>
                        All possible events were executed by at least one
                        player.
                      </p>
                    )}
                    <div
                      className='fst-italic'
                      style={{ fontFamily: 'monospace' }}>
                      {
                        part2_Day1_NonExecutedEvents.length > 0 &&
                          part2_Day1_NonExecutedEvents.map((ne, i) => (
                            <span
                              key={i}
                              className='badge text-bg-light border me-3 mb-2'>
                              {ne}
                            </span>
                          ))
                        // <ul>
                        //   {part2_Day1_NonExecutedEvents.map((ne, i) => (
                        //     <li key={i}>{ne}</li>
                        //   ))}
                        // </ul>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Histogram: Part 2 - Day 45 */}
            <div className='col-12 mt-5'>
              <div className='card bg-light p-3'>
                <h3 className='fs-5 mb-3'>Part 2 - Day 45: Actions</h3>

                <HighchartsReact
                  // ref={this.chartRef}
                  highcharts={Highcharts}
                  options={{
                    title: { text: '' },
                    chart: { type: 'column' },
                    credits: { enabled: false },
                    legend: { enabled: true },
                    xAxis: {
                      // title: { text: 'Employee Effort' },
                      labels: { style: { fontSize: '14px' } },
                      categories: part2_Day45_Decisions,
                    },
                    yAxis: {
                      labels: { style: { fontSize: '14px' } },
                      title: { text: 'Count' },
                      allowDecimals: false,
                    },
                    // tooltip: {
                    //   formatter: function () {
                    //     const xLabel = this?.x === 50 ? '≥50' : this?.x;
                    //     return (
                    //       `<b>Employee Effort</b>: ${xLabel}<br>` +
                    //       `<b>Cumulative Frequency</b>: ${this.y}`
                    //     );
                    //   },
                    // },
                    series: [
                      {
                        name: 'Events',
                        data: part2_Day45_Decisions_Frequency,
                      },
                    ],
                  }}
                />

                {/* Non-executed events */}
                <div className='card mt-3'>
                  <div className='card-body'>
                    <h5 className='fs-6 fw-bold'>Actions No One Pursued</h5>
                    <hr />
                    {part2_Day45_NonExecutedEvents.length === 0 && (
                      <p>
                        All possible events were executed by at least one
                        player.
                      </p>
                    )}
                    <div
                      className='fst-italic'
                      style={{ fontFamily: 'monospace' }}>
                      {
                        part2_Day45_NonExecutedEvents.length > 0 &&
                          part2_Day45_NonExecutedEvents.map((ne, i) => (
                            <span
                              key={i}
                              className='badge text-bg-light border me-3 mb-2'>
                              {ne}
                            </span>
                          ))
                        // <ul>
                        //   {part2_Day45_NonExecutedEvents.map((ne, i) => (
                        //     <li key={i}>{ne}</li>
                        //   ))}
                        // </ul>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameHistogram;
